import {
  Component,
  Element,
  Env,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { zapierURL } from "../../api/config";
import { UTMObject, utmObjects } from "../../api/events";
import {
  fetchOidcEnabled,
  fetchSlugAndSelectedAPI,
} from "../../api/repository";
import { getAnchorTarget, LinkTarget } from "../../lib/link-target";
import { Logger } from "../../lib/logger";
import { mediaColorSchemeQuery } from "../../lib/mediaQueries";
import { ModalOpen } from "../../lib/modal";
import { rootElementMetadata } from "../../lib/root-element-metadata";
import { referrerWithUTMs } from "../../lib/referrer";
import * as MZ from "../../lib/manage-zaps";
import { hasValidQuacParams } from "../../lib/quac";
import * as WFE from "../../lib/workflow-element";
import { QuacInfo } from "../../components";
import { browser } from "../../lib/userAgent";

// TODO: This module assumes only one Manage Zaps will be on each page. It
// should be able to understand if the `postMessage` came from the _specific_
// iframe in question. We should probably generate a UUID in this module and
// send it to the zapier-internal-manage-zaps-iframe, so it can send that back
// to us in each message. Unless there's an easier way to tell... maybe we could
// use `event.source`?

// TODO: The Manage Zaps element doesn't seem to update the `clientId` inside
// the iframe if it's changed outside the iframe.

type LoggedIn = {
  // Intentionally blank for now
};

type LoggedOut = {
  // Intentionally blank for now
};

@Component({
  tag: "zapier-internal-manage-zaps",
  styleUrl: "zapier-internal-manage-zaps.css",
  shadow: true,
})
export class ZapierInternalManageZaps {
  @Element() private elem!: HTMLZapierInternalManageZapsElement;

  /**
   * Enable dark mode, or auto to go between light and dark modes depending on
   * the user's client setting
   */
  @Prop() theme: "light" | "dark" | "auto" = "light";

  /**
   * Client ID used to identify your Zapier app
   */
  @Prop() clientId = "";

  /**
   * [INTERNAL] UTM parameters
   */
  @Prop() internalUtm: UTMObject = utmObjects.workflowElement__fallbackValue;

  /** Define if embed will open on a popup or iframe. Currently, default to iframe
   * since auto mode is not supported yet. It'll be implemented on TPS-3086.
   */
  @Prop({ reflect: true }) embedMode: WFE.EmbedMode | "auto" = "auto";

  @Prop() internalModalState: WFE.ModalState = { type: "closed" };

  /** Open links in a new tab or the same tab */
  @Prop() linkTarget: LinkTarget = "new-tab";

  /** [INTERNAL] Zapier service slug */
  @Prop({ mutable: true }) internalSlug = "";

  /** [INTERNAL] Zapier service selected API */
  @Prop({ mutable: true }) internalSelectedAPI = "";

  /** [INTERNAL] Zapier service selected API */
  @Prop({ reflect: false }) internalFromWorkflowElement = false;

  /** Zapier Quac props */
  @Prop({ reflect: true }) signUpFirstName = "";

  @Prop({ reflect: true }) signUpLastName = "";

  @Prop({ reflect: true }) signUpEmail = "";

  /** Zapier service OIDC enabled */
  @State() private oidcEnabled: undefined | boolean;

  @State() private _iframeState: "loading" | "loaded" | "ready" = "loading";

  @State() private computedTheme: "light" | "dark" = "light";

  /** [INTERNAL] Event for when a modal is requested to be opened */
  @Event() internalModalOpen!: EventEmitter<ModalOpen>;

  /** [INTERNAL] Event for when the user is logged in */
  @Event() internalLoggedIn!: EventEmitter<LoggedIn>;

  /** [INTERNAL] Event for when the user is logged out */
  @Event() internalLoggedOut!: EventEmitter<LoggedOut>;

  /** [INTERNAL] Event for letting popup know about updates */
  @Event() internalSendActionToPopup!: EventEmitter<MZ.Action>;

  /** [INTERNAL] Method to refresh the list of Zaps
   *
   * This method is async, but it does NOT wait for the Zaps to finish
   * refreshing before resolving. It may in the future, but nothing is promised
   * right now.
   */
  @Method()
  async internalRefreshZaps() {
    this._sendAction({ type: "zapier-elements.workflow.action.refresh" });
  }

  private _iframe?: HTMLIFrameElement;

  private _setIframe = (iframe?: HTMLIFrameElement) => {
    this._iframe = iframe;
  };

  private _setCalculatedTheme = () => {
    if (this.theme === "auto") {
      this.computedTheme = mediaColorSchemeQuery.matches ? "dark" : "light";
    } else {
      this.computedTheme = this.theme;
    }
  };

  @Watch("clientId")
  async setSlugAndSelectedAPI() {
    // Fetch slug and selected API only if we don't have them already
    if (this.internalSlug && this.internalSelectedAPI) {
      return;
    }
    const { slug, selectedAPI } = await fetchSlugAndSelectedAPI({
      clientID: this.clientId,
    });
    this.internalSlug = slug;
    this.internalSelectedAPI = selectedAPI;
  }

  async setOidcEnabled() {
    if (
      this.internalSlug &&
      this.oidcEnabled === undefined &&
      !this.internalDisableAAC
    ) {
      this.oidcEnabled = await fetchOidcEnabled(this.internalSlug);
    }
  }

  isQuacEnabled() {
    return hasValidQuacParams(this.quacInfo).isValid;
  }

  private get quacInfo(): QuacInfo {
    return {
      clientId: this.clientId,
      signUpFirstName: this.signUpFirstName,
      signUpLastName: this.signUpLastName,
      signUpEmail: this.signUpEmail,
    };
  }

  @Watch("theme")
  appThemeChanged() {
    this._setCalculatedTheme();
  }

  connectedCallback() {
    this._setCalculatedTheme();
    mediaColorSchemeQuery.addEventListener("change", this._setCalculatedTheme);
  }

  disconnectedCallback() {
    mediaColorSchemeQuery.removeEventListener(
      "change",
      this._setCalculatedTheme,
    );
  }

  private _onIframeLoad = () => {
    this._iframeState = "loaded";
  };

  private get _computedEmbedMode(): WFE.EmbedMode {
    switch (this.embedMode) {
      case "iframe":
      case "popup": {
        return this.embedMode;
      }
      default: {
        // If we're in an iframe, we need to check to see if there is the clientID is present,
        // because if so it's on the partner to correctly allowlist the domains they are trying to
        // nest in, but if not we should use a popup
        if (window.top !== window.self) {
          return this.clientId !== undefined ? "iframe" : "popup";
        }
        // QuAC is broken in Safari when clicking a Zap Template, so let's force
        // the use of popup mode
        if (browser === "safari" && hasValidQuacParams(this.quacInfo).isValid) {
          return "popup";
        }
        return "iframe";
      }
    }
  }

  @Listen("message", { target: "window" })
  onMessage(event: MessageEvent) {
    // Ignore messages from other origins
    if (event.origin !== zapierURL) {
      return;
    }

    const isIframe = event.source === this._iframe?.contentWindow;

    const isPopup =
      this.internalModalState.type === "popup" &&
      this.internalModalState.popup === event.source;

    // Ignore messages from other iframes
    if (!(isIframe || isPopup)) {
      return;
    }

    const message: MZ.Update = event.data;
    switch (message.type) {
      case "zapier-elements.workflow.update.loaded": {
        this._iframeState = "ready";
        this.callSendActionWithProps();
        break;
      }
      case "zapier-elements.workflow.update.height-changed": {
        if (this._iframe) {
          this._iframe.style.height = message.height + "px";
        }
        break;
      }
      case "zapier-elements.workflow.update.modal-open": {
        this.internalModalOpen.emit({ url: message.url });
        break;
      }
      case "zapier-elements.workflow.update.logged-in": {
        this.internalLoggedIn.emit({});
        break;
      }
      case "zapier-elements.workflow.update.logged-out": {
        this.internalLoggedOut.emit({});
        break;
      }
    }
  }

  private _sendAction(action: MZ.Action): void {
    if (this._computedEmbedMode === "popup") {
      this.internalSendActionToPopup.emit(action);
      return;
    }

    const win = this._iframe?.contentWindow;
    if (win && this._iframeState !== "loading") {
      win.postMessage(action, zapierURL);
    }
  }

  private callSendActionWithProps() {
    this._sendAction({
      type: "zapier-elements.workflow.action.update",
      props: {
        // For now popup should always be light... maybe we'll support dark mode
        // popups later!
        theme:
          this._computedEmbedMode === "popup" ? "light" : this.computedTheme,
        clientID: this.clientId,
        signUpFirstName: this.signUpFirstName,
        signUpLastName: this.signUpLastName,
        signUpEmail: this.signUpEmail,
        slug: this.internalSlug,
        selectedAPI: this.internalSelectedAPI,
        oidcEnabled: !!this.oidcEnabled,
        quacEnabled: this.isQuacEnabled(),
        linkTarget: this.linkTarget,
        referrer: this.referrer,
        internalFromWorkflowElement: this.internalFromWorkflowElement,
        internalPopupBlockerEnabled: this.internalPopupBlocker,
        internalDemoMode: this.internalDemoMode,
        rootElementMetadata,
        utm: this.internalUtm,
      },
    });
  }

  componentWillLoad() {
    this.appThemeChanged();

    const setStateAsynchronously = async () => {
      await this.setSlugAndSelectedAPI();
      await this.setOidcEnabled();
    };

    setStateAsynchronously();
  }

  componentDidRender() {
    // Set a console warning message if link-target="same-frame" is used for the ZME
    if (this.linkTarget === "same-frame") {
      Logger.warn(
        "link-target=same-frame is not supported for the Zapier Workflow Element. Please use either new-tab or same-tab for link-target instead.",
      );
    }

    this.callSendActionWithProps();
  }

  private get referrer() {
    return referrerWithUTMs(location.href, this.internalUtm);
  }

  private get internalPopupBlocker() {
    return "internalPopupBlocker" in this.elem.dataset;
  }

  private get internalDemoMode(): MZ.InternalDemoMode {
    return this.elem.dataset.internalDemoMode as MZ.InternalDemoMode;
  }

  private get internalDisableAAC(): boolean {
    return "internalDisableAac" in this.elem.dataset;
  }

  private get internalTestIframeBlocked(): boolean {
    return "internalTestIframeBlocked" in this.elem.dataset;
  }

  render() {
    const url = new URL("/partner/iframe/workflow-element", zapierURL);
    if (this.clientId !== undefined) {
      url.searchParams.set("client_id", this.clientId);
    }
    if (this.internalTestIframeBlocked) {
      url.pathname = "/partner/iframe/blocked";
    }
    const hostnameOverride = this.elem.dataset.internalSdkHostname;
    if (hostnameOverride) {
      url.searchParams.append("preview", hostnameOverride);
    }
    // Bust cache by adding the git commit SHA to the URL parameter. It seems
    // like otherwise the iframe content can load cached JS & CSS.
    url.searchParams.append("sha", Env.commitSHA || "");

    if (this._computedEmbedMode === "popup") {
      const newWindowText =
        this.linkTarget === "new-tab" && "(opens in new window)";

      const url = new URL("/partner/iframe/workflow-element", zapierURL);

      return (
        <Host {...rootElementMetadata} data-theme={this.computedTheme}>
          <a
            aria-label={`Create workflow ${newWindowText}`}
            class="button"
            href={url.href}
            target={getAnchorTarget(this.linkTarget)}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              this.internalModalOpen.emit({
                url: url.href,
              });
            }}
          >
            Open workflows
          </a>
        </Host>
      );
    }

    if (this._computedEmbedMode === "iframe") {
      return (
        <Host {...rootElementMetadata} data-theme={this.computedTheme}>
          <div class="tooltip" hidden={!this.internalDemoMode}>
            An example of what users see after creating workflows with your app.
          </div>
          <iframe
            title="Sign up or log in to Zapier to see your automated workflows"
            referrerPolicy="origin"
            class="iframe"
            src={url.href}
            onLoad={this._onIframeLoad}
            ref={this._setIframe}
          />
        </Host>
      );
    }
  }
}
